import { z } from "zod";

export const basicClinicSettingsSchema = z.object({
  clinicName: z.string().min(3, "Nombre requerido"),
  contactEmail: z.string().email("Email inválido"),
  contactPhone: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  zipCode: z.string().optional().nullable(),
});

export const businessConfSchema = z.object({
  countryCode: z.string().optional().default("ES"),
  language: z.string().optional().default("es"),
  timezone: z.string().min(1).default("Europe/Madrid"),
  allowedLanguages: z.array(z.string()).optional().default(["es"]),
  currency: z.string().default("EUR"),
  taxName: z.string().default("IVA"),
  taxDefault: z.coerce.number().min(0).max(100).default(21),
  allowedTaxRates: z.array(z.number()).optional().default([0, 4, 10, 21]),
  cancellationPolicy: z
    .string()
    .default("Cancelación gratuita hasta 24h antes."),
});

export const calendarConfSchema = z.object({
  // Usamos coerce.number para que "15" pase a 15 automáticamente
  slotInterval: z.coerce
    .number()
    .refine((val) => [5, 10, 15, 20, 30, 60].includes(val), {
      message: "Intervalo no permitido",
    }),
  maxFutureDays: z.coerce.number().int().min(1).default(60),
  minNoticeHours: z.coerce.number().int().min(0).default(2),
  defaultDuration: z.coerce.number().int().min(1).default(60),
  // Magia de Zod: Si recibe un string, lo convierte a array antes de validar
  allowedDurations: z.preprocess((val) => {
    if (typeof val === "string") return val.split(",").map(Number);
    return val; // Si ya es array, déjalo pasar
  }, z.array(z.number().int()).min(1)),
  defaultBufferTime: z.coerce.number().int().min(0).default(0),
  holidays: z.array(z.string()).default([]),
  // Cambiamos businessHours por weeklySchedule para que coincida con el form
  weeklySchedule: z
    .record(
      z.string(),
      z.array(
        z.object({
          start: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/),
          end: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/),
        }),
      ),
    )
    .optional(),
});

export const brandingSchema = z.object({
  logoUrl: z.string().url().nullable().optional(),
  faviconUrl: z.string().url().nullable().optional(),
  // Permitimos null y hacemos que el regex sea opcional
  primaryColor: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)
    .nullable()
    .optional(),
  secondaryColor: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)
    .nullable()
    .optional(),
  backgroundColor: z
    .string()
    .regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)
    .nullable()
    .optional(),
  fontFamily: z.string().nullable().optional().default("sans"),
  borderRadius: z.coerce
    .number()
    .int()
    .min(0)
    .max(32)
    .nullable()
    .optional()
    .default(8),
});

export const flagsSchema = z.object({
  onlineBooking: z.boolean().default(true),
  showPrices: z.boolean().default(true),
  requiresApproval: z.boolean().default(false),
  sendReminders: z.boolean().default(true),
  emailNotifications: z.boolean().default(true),
  whatsappNotifications: z.boolean().default(false),
  collectPaymentAtBooking: z.boolean().default(false),
});

export const updateClinicSettingsSchema = z.object({
  businessConf: businessConfSchema.optional(),
  calendarConf: calendarConfSchema.optional(),
  branding: brandingSchema.optional(),
  flags: flagsSchema.optional(),
});
